// skeleton/app/src/components/AppHeaderComponent/AppHeaderComponent.tsx

import { Menubar } from 'primereact/menubar';
import { MenuItem } from 'primereact/menuitem';
import { useLocation, useNavigate } from 'react-router';
import './AppHeaderComponent.scss';
import '../Common/Tooltip.scss';
import { useAuthContext } from '../../Context/AuthContext';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import { ProviderTypeOption, useAppContext } from '../../Context';
import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';

interface CurrentReviewYearDetails {
    reviewYear?: number;
}

export const AppHeaderComponent = () => {
    const {
        signOut,
        isAuthenticated,
        isSuperAdministrator
    } = useAuthContext();

    const navigate = useNavigate();

    const {
        setReviewYear,
        setProviderTypes,
        providerType,
        setProviderType,
        providerTypes,
        reviewYear
    } = useAppContext();

    const [visible, setVisible] = useState(false);
    const [sidebarVisibility, setSidebarVisibility] = useState(false);

    const location = useLocation();

    const navigateToUrl = (
        url: string,
        additionalState?: Record<string, unknown>
    ) => {
        try {
            navigate(url, {
                state: {
                    providerType,
                    reviewYear,
                    ...additionalState
                }
            });

            setSidebarVisibility(false);
        } catch (error) {
            console.error(
                '[AppHeaderComponent] Navigation failed.',
                error
            );
        }
    };

    let menuDefs: MenuItem[];

    if (isAuthenticated) {
        menuDefs = [
            { label: 'Home', url: '/pages/home' },
            { label: 'Help', url: '/help' },
            { label: 'Contact Us', url: '/contact' },
            { label: 'Users', url: '/users' },
            {
                label: 'Logout',
                command: () => {
                    setVisible(true);
                }
            }
        ];
    } else {
        menuDefs = [
            { label: 'Help', url: '/tableau' },
            { label: 'Contact Us', url: '/contact' }
        ];
    }

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        const fetchProviderTypes = async () => {
            try {
                const response =
                    await httpService.get<ProviderTypeOption[]>(
                        ApiEndpoints.GetProviderTypes
                    );

                const types = response?.data || [];

                setProviderTypes(types);

                if (types.length === 0) {
                    return;
                }

                const currentType = types.find(
                    (type: ProviderTypeOption) =>
                        type.providerCode?.toUpperCase() ===
                        providerType?.toUpperCase()
                );

                const residentialType = types.find(
                    (type: ProviderTypeOption) =>
                        type.providerCode === Constants.RSKey
                );

                if (currentType) {
                    setProviderType(currentType.providerCode);
                    return;
                }

                if (residentialType) {
                    setProviderType(Constants.RSKey);
                    return;
                }

                setProviderType(types[0].providerCode);
            } catch (error) {
                console.error(
                    '[AppHeaderComponent] Failed to fetch provider types.',
                    error
                );
            }
        };

        void fetchProviderTypes();
    }, [
        isAuthenticated,
        providerType,
        setProviderType,
        setProviderTypes
    ]);

    useEffect(() => {
        if (!isAuthenticated || !providerType) {
            return;
        }

        const fetchReviewYear = async () => {
            try {
                const response =
                    await httpService.get<CurrentReviewYearDetails>(
                        `${ApiEndpoints.GetCurrentReviewYearDetails}/${providerType}`
                    );

                const currentReviewYear =
                    response?.data?.reviewYear;

                if (typeof currentReviewYear === 'number') {
                    setReviewYear(currentReviewYear);
                }
            } catch (error) {
                console.error(
                    '[AppHeaderComponent] Failed to fetch current review year.',
                    error
                );
            }
        };

        void fetchReviewYear();
    }, [
        providerType,
        isAuthenticated,
        setReviewYear
    ]);

    const menuItems: MenuItem[] = menuDefs.map((item) => ({
        label: item.label,
        command: item.command,
        template: () => (
            <a
                className='p-menuitem-link'
                style={{
                    cursor: 'pointer',
                    textDecoration: 'none'
                }}
                onClick={(event) => {
                    event.preventDefault();

                    if (item.url) {
                        navigateToUrl(item.url);
                        return;
                    }

                    if (typeof item.command === 'function') {
                        item.command({
                            originalEvent: event,
                            item
                        });
                    }
                }}
            >
                <span
                    className={
                        location.pathname === item.url
                            ? 'p-menuitem-text active-menu-item'
                            : 'p-menuitem-text'
                    }
                >
                    {item.label}
                </span>
            </a>
        )
    }));

    const providerMenuString =
        providerTypes.find(
            (type: ProviderTypeOption) =>
                type.providerCode === providerType
        );

    const isScoProvider =
        providerType === Constants.SCOKey;

    const isResidentialProvider =
        providerType === Constants.RSKey;

    return (
        <>
            <div className="card flex justify-content-center">
                <Sidebar
                    visible={sidebarVisibility}
                    onHide={() => setSidebarVisibility(false)}
                    className="app-navigation-sidebar"
                    style={{
                        backgroundColor: 'rgb(28, 47, 57)'
                    }}
                    content={({ hide }) => (
                        <div
                            className="app-navigation-sidebar-content"
                            style={{
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <div>
                                <span
                                    style={{
                                        padding: '2px 2px 2px 0',
                                        float: 'right'
                                    }}
                                >
                                    <Button
                                        className='p-button-tertiary'
                                        type="button"
                                        onClick={(e) => hide(e)}
                                        icon="pi pi-times"
                                        size="small"
                                        style={{
                                            backgroundColor: 'inherit',
                                            border: 'none'
                                        }}
                                    />
                                </span>
                            </div>

                            <div className="sidebar-menu-header">
                                {isScoProvider ? (
                                    <>
                                        Support Coordination
                                        <br />
                                        Organization Menu
                                    </>
                                ) : providerMenuString?.providerType ? (
                                    `${providerMenuString.providerType} Provider Menu`
                                ) : (
                                    'Provider Menu'
                                )}
                            </div>

                            <div style={{ padding: '5px 0px 5px 0px' }}>
                                <Button
                                    className={`p-button-tertiary sidebar-menu-item${
                                        location.pathname === '/standards'
                                            ? ' sidebar-active'
                                            : ''
                                    }`}
                                    label='Standards & Measures Library'
                                    size="large"
                                    icon='pi pi-standard-icon'
                                    style={{
                                        width: '100%',
                                        backgroundColor: 'rgb(28, 47, 57)',
                                        border: 'none',
                                        textAlign: 'left'
                                    }}
                                    onClick={() =>
                                        navigateToUrl('/standards')
                                    }
                                />

                                <Button
                                    className={`p-button-tertiary sidebar-menu-item${
                                        location.pathname === '/dataupload/import'
                                            ? ' sidebar-active'
                                            : ''
                                    }`}
                                    label='Data Upload Center'
                                    size="large"
                                    icon='pi pi-upload-icon'
                                    style={{
                                        width: '100%',
                                        backgroundColor: 'rgb(28, 47, 57)',
                                        border: 'none',
                                        textAlign: 'left'
                                    }}
                                    onClick={() =>
                                        navigateToUrl('/dataupload/import')
                                    }
                                />

                                <Button
                                    className={`p-button-tertiary sidebar-menu-item${
                                        location.pathname === '/dataupload/notice'
                                            ? ' sidebar-active'
                                            : ''
                                    }`}
                                    label='Provider Data & Notices'
                                    size="large"
                                    icon='pi pi-notice-icon'
                                    style={{
                                        width: '100%',
                                        backgroundColor: 'rgb(28, 47, 57)',
                                        border: 'none',
                                        textAlign: 'left'
                                    }}
                                    onClick={() =>
                                        navigateToUrl('/dataupload/notice')
                                    }
                                />

                                {isSuperAdministrator() && (
                                    <Button
                                        className={`p-button-tertiary sidebar-menu-item${
                                            location.pathname === '/admin-console'
                                                ? ' sidebar-active'
                                                : ''
                                        }`}
                                        label='Admin Console'
                                        size="large"
                                        icon='pi pi-admin-console-icon'
                                        style={{
                                            width: '100%',
                                            backgroundColor: 'rgb(28, 47, 57)',
                                            border: 'none',
                                            textAlign: 'left'
                                        }}
                                        onClick={() =>
                                            navigateToUrl('/admin-console')
                                        }
                                    />
                                )}

                                {isSuperAdministrator() && (
                                    <Button
                                        className={`p-button-tertiary sidebar-menu-item${
                                            location.pathname === '/question-mapping'
                                                ? ' sidebar-active'
                                                : ''
                                        }`}
                                        label='Question Mapping'
                                        size="large"
                                        icon='pi pi-link'
                                        style={{
                                            width: '100%',
                                            backgroundColor: 'rgb(28, 47, 57)',
                                            border: 'none',
                                            textAlign: 'left'
                                        }}
                                        onClick={() => navigateToUrl('/question-mapping')}
                                    />
                                )}
                            </div>

                            <div style={{ padding: '5px 0px 5px 0px' }}>
                                <div className="sidebar-category-title">
                                    PBC Resources
                                </div>

                                <Button
                                    className={`p-button-tertiary sidebar-menu-item${
                                        location.pathname === '/dashboard'
                                            ? ' sidebar-active'
                                            : ''
                                    }`}
                                    label='PBC Scoring Portal'
                                    size="large"
                                    icon='pi pi-dash-icon'
                                    style={{
                                        width: '100%',
                                        backgroundColor: 'rgb(28, 47, 57)',
                                        border: 'none',
                                        textAlign: 'left'
                                    }}
                                    onClick={() =>
                                        navigateToUrl('/dashboard')
                                    }
                                />

                                <Button
                                    className={`p-button-tertiary sidebar-menu-item${
                                        location.pathname === '/tableau'
                                            ? ' sidebar-active'
                                            : ''
                                    }`}
                                    label='PBC Insights Center'
                                    size="large"
                                    icon='pi pi-insight-icon'
                                    style={{
                                        width: '100%',
                                        backgroundColor: 'rgb(28, 47, 57)',
                                        border: 'none',
                                        textAlign: 'left'
                                    }}
                                    onClick={() =>
                                        navigateToUrl(
                                            '/tableau',
                                            {
                                                portal:
                                                    isResidentialProvider
                                                        ? Constants.PBC_RS_TABLEAU_URL_KEY
                                                        : Constants.PBC_TABLEAU_URL_KEY
                                            }
                                        )
                                    }
                                />
                            </div>

                            <div style={{ padding: '5px 0px 5px 0px' }}>
                                <div className="sidebar-category-title">
                                    P4P Resources
                                </div>

                                <Button
                                    className={`p-button-tertiary sidebar-menu-item${
                                        location.pathname === '/pfp/providers'
                                            ? ' sidebar-active'
                                            : ''
                                    }`}
                                    label='P4P Scoring Portal'
                                    size="large"
                                    icon='pi pi-dash-icon'
                                    style={{
                                        width: '100%',
                                        backgroundColor: 'rgb(28, 47, 57)',
                                        border: 'none',
                                        textAlign: 'left'
                                    }}
                                    onClick={() =>
                                        navigateToUrl('/pfp/providers')
                                    }
                                />

                                <Button
                                    className={`p-button-tertiary sidebar-menu-item${
                                        location.pathname === '/p4p/dashboard'
                                            ? ' sidebar-active'
                                            : ''
                                    }`}
                                    label='P4P Insights Center'
                                    size="large"
                                    icon='pi pi-insight-icon'
                                    style={{
                                        width: '100%',
                                        backgroundColor: 'rgb(28, 47, 57)',
                                        border: 'none',
                                        textAlign: 'left'
                                    }}
                                    onClick={() =>
                                        navigateToUrl(
                                            '/p4p/dashboard',
                                            {
                                                portal:
                                                    Constants.P4P_TABLEAU_URL_KEY
                                            }
                                        )
                                    }
                                />

                                {isSuperAdministrator() && (
                                    <Button
                                        className={`p-button-tertiary sidebar-menu-item${
                                            location.pathname === '/data-management'
                                                ? ' sidebar-active'
                                                : ''
                                        }`}
                                        label='Data Management'
                                        size="large"
                                        icon='pi pi-database'
                                        style={{
                                            width: '100%',
                                            backgroundColor: 'rgb(28, 47, 57)',
                                            border: 'none',
                                            textAlign: 'left',
                                            paddingLeft: 0
                                        }}
                                        onClick={() =>
                                            navigateToUrl('/data-management')
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    )}
                />
            </div>

            <header className="header menubar-right-container">
                {location.pathname !== '/' && (
                    <Button
                        className='sidebaricon'
                        icon="pi pi-bars"
                        style={{
                            backgroundColor: 'rgb(28, 47, 57)',
                            border: 'none',
                            borderRadius: '0px'
                        }}
                        onClick={() =>
                            setSidebarVisibility(true)
                        }
                        title='Menu'
                    />
                )}

                <span
                    style={{
                        fontSize: '18px',
                        fontWeight: 900,
                        color: '#3774b9',
                        cursor: 'pointer',
                        padding: '0 15px'
                    }}
                    onClick={() =>
                        navigateToUrl('/pages/home')
                    }
                    className="app-logo"
                >
                    MyPBC Portal
                </span>

                <Menubar
                    model={menuItems}
                    className="hover"
                />

                <Dialog
                    header="Confirmation"
                    visible={visible}
                    position='top'
                    style={{
                        marginTop: '50px'
                    }}
                    draggable={false}
                    resizable={false}
                    onHide={() =>
                        setVisible(false)
                    }
                    footer={
                        <div className='card flex flex-wrap justify-content-center gap-3'>
                            <Button
                                label="Yes"
                                icon="pi pi-check"
                                onClick={() => {
                                    try {
                                        setVisible(false);
                                        signOut();
                                        navigate('/logout');
                                    } catch (error) {
                                        console.error(
                                            '[AppHeaderComponent] Logout failed.',
                                            error
                                        );
                                    }
                                }}
                                autoFocus
                            />

                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                severity='secondary'
                                onClick={() =>
                                    setVisible(false)
                                }
                            />
                        </div>
                    }
                >
                    Are you sure you want to logout?
                </Dialog>
            </header>
        </>
    );
};

export default AppHeaderComponent;